import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button, TextField } from "@mui/material";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import PostAddOutlinedIcon from "@mui/icons-material/PostAddOutlined";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { moneyAud, formatDate, formatDateTime } from "../../utils/portalHelpers";
import {
  PageHeader,
  Panel,
  StatCard,
  StatusChip,
  EmptyState,
  LoadingPanel,
  ErrorPanel,
  chipTone,
} from "../../components/ui";
import { COMMISSION_STATUSES, labelFor, toneFor } from "const/referralEnums";
import { CLIENT_TABS } from "../../navigation";
import { partnerQueryKeys } from "../../api/queryKeys";
import { fetchPartnerClient } from "../../api/endpoints/workspace";
import { fetchPartnerMessages, sendPartnerMessage } from "../../api/endpoints/messages";
import usePartnerQuery from "../../hooks/usePartnerQuery";
import ClientApplicationsTab, { RequestDialog } from "./ClientApplicationsTab";
import { createPartnerApplicationRequest } from "../../api/endpoints/applications";
import ClientDocumentsPanel from "./ClientDocumentsPanel";
import BookAppointmentDialog from "../Appointments/BookAppointmentDialog";
import { requestStage } from "../Documents/documentHelpers";

const Field = ({ label, value }) => (
  <div>
    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 m-0">{label}</p>
    <p className="text-sm text-slate-900 m-0 mt-0.5">{value || "—"}</p>
  </div>
);

const ClientDetailPage = () => {
  const { clientId } = useParams();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(CLIENT_TABS[0].id);
  const [draft, setDraft] = useState("");
  const [booking, setBooking] = useState(false);
  const [applying, setApplying] = useState(false);

  const createApplication = useMutation((body) => createPartnerApplicationRequest(clientId, body), {
    onSuccess: () => {
      setApplying(false);
      toast.success("Sent to the office for review");
      queryClient.invalidateQueries(partnerQueryKeys.applications(clientId));
      queryClient.invalidateQueries(partnerQueryKeys.applicationRequests({ clientId }));
      queryClient.invalidateQueries(partnerQueryKeys.applicationRequests({}));
      // Show where the new request now sits.
      setTab("applications");
    },
  });

  const messageParams = useMemo(() => ({ page: 0, size: 50, clientId }), [clientId]);

  const {
    data: client,
    isLoading,
    isError,
    refetch,
  } = usePartnerQuery(partnerQueryKeys.client(clientId), () => fetchPartnerClient(clientId), {
    enabled: Boolean(clientId),
  });

  // The thread loads only when its tab is selected: opening a client to check an
  // application or a document no longer costs a message fetch.
  const { data: thread } = usePartnerQuery(
    partnerQueryKeys.messages(messageParams),
    () => fetchPartnerMessages(messageParams),
    { enabled: Boolean(clientId) && tab === "messages" }
  );

  const send = useMutation(sendPartnerMessage, {
    onSuccess: () => {
      setDraft("");
      queryClient.invalidateQueries(partnerQueryKeys.messages(messageParams));
    },
  });

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Loading…" />
        <LoadingPanel label="Loading this client…" />
      </div>
    );
  }

  if (isError || !client) {
    return (
      <div>
        <PageHeader
          title="Not available"
          actions={
            <Link
              to="/partner/clients"
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold no-underline text-slate-700"
            >
              Back to clients
            </Link>
          }
        />
        <ErrorPanel
          description="This client is not yours, or the server did not answer."
          action={
            <button
              type="button"
              onClick={() => refetch()}
              className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white"
            >
              Try again
            </button>
          }
        />
      </div>
    );
  }

  const commissions = client.commissions || [];
  const documentRequests = client.documentRequests || [];
  const messages = thread?.content || [];
  const documentsToCollect = documentRequests.filter((request) =>
    ["collecting", "ready"].includes(requestStage(request).id)
  ).length;
  const documentsSent = documentRequests.filter(
    (request) => requestStage(request).id === "sent"
  ).length;
  const tabBadges = { documents: documentsToCollect };

  return (
    <div>
      <div className="mb-5">
        <Link
          to="/partner/clients"
          className="text-xs font-semibold text-teal-700 no-underline hover:underline"
        >
          ← All clients
        </Link>
        <div className="mt-1.5 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="m-0 text-xl font-bold text-slate-900">{client.name}</h2>
              <StatusChip tone={client.status === "ACTIVE" ? "emerald" : "neutral"}>
                {client.status === "ACTIVE" ? "Active" : "Inactive"}
              </StatusChip>
            </div>
            <p className="m-0 mt-0.5 text-sm text-slate-500">
              {client.modules?.length ? client.modules.join(" · ") : "No modules recorded yet"}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="contained"
              size="small"
              disableElevation
              startIcon={<PostAddOutlinedIcon />}
              onClick={() => {
                createApplication.reset();
                setApplying(true);
              }}
              sx={{ bgcolor: "#0f766e", "&:hover": { bgcolor: "#115e59" } }}
            >
              New application
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<EventAvailableOutlinedIcon />}
              onClick={() => setBooking(true)}
            >
              Book appointment
            </Button>
          </div>
        </div>
      </div>

      <RequestDialog
        open={applying}
        initial={null}
        clientName={client.name}
        onClose={() => setApplying(false)}
        onSaved={(body) => createApplication.mutate(body)}
        saving={createApplication.isLoading}
        error={createApplication.isError}
      />

      <BookAppointmentDialog
        open={booking}
        onClose={() => setBooking(false)}
        initialClients={[{ clientId: Number(clientId), name: client.name }]}
      />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        <StatCard
          label="Documents to collect"
          value={documentsToCollect}
          tone={documentsToCollect ? "warn" : "default"}
        />
        <StatCard label="Sent to office" value={documentsSent} />
        <StatCard label="Your commission" value={moneyAud(client.myCommission)} tone="good" />
      </div>

      <div
        role="tablist"
        aria-label="Client sections"
        className="mb-5 flex gap-6 overflow-x-auto border-b border-slate-200"
      >
        {CLIENT_TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            onClick={() => setTab(item.id)}
            className={[
              "-mb-px inline-flex items-center gap-1.5 whitespace-nowrap border-0 border-b-2 bg-transparent px-1 pb-2.5 text-sm font-semibold cursor-pointer",
              tab === item.id
                ? "border-teal-600 text-teal-800"
                : "border-transparent text-slate-500 hover:text-slate-800",
            ].join(" ")}
          >
            {item.label}
            {tabBadges[item.id] ? (
              <span className="rounded-full bg-amber-100 px-1.5 text-[10px] font-bold tabular-nums text-amber-800">
                {tabBadges[item.id]}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {tab === "details" ? (
        <Panel title="Details">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Name" value={client.name} />
            <Field label="Status" value={client.status === "ACTIVE" ? "Active" : "Inactive"} />
            <Field label="Email" value={client.email} />
            <Field label="Phone" value={client.phone} />
            <Field label="With you since" value={formatDate(client.createdAt)} />
          </div>
        </Panel>
      ) : null}

      {tab === "applications" ? (
        <ClientApplicationsTab clientId={clientId} clientName={client?.name} />
      ) : null}

      {tab === "commissions" ? (
        <Panel title="Your commission on this client">
          {commissions.length ? (
            <ul className="list-none p-0 m-0">
              {commissions.map((row) => (
                <li
                  key={row.id}
                  className="flex items-center justify-between gap-3 py-2.5 border-b border-slate-100 last:border-0"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 m-0 truncate">
                      {row.moduleLabel || "Referral"}
                    </p>
                    <p className="text-xs text-slate-500 m-0 mt-0.5">{formatDate(row.earnedAt)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusChip tone={chipTone(toneFor(COMMISSION_STATUSES, row.status))}>
                      {labelFor(COMMISSION_STATUSES, row.status)}
                    </StatusChip>
                    <span className="text-sm tabular-nums">{moneyAud(row.amount)}</span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              title="Nothing earned on this client yet"
              description="A referral amount recorded on any of their services will appear here."
            />
          )}
        </Panel>
      ) : null}

      {tab === "documents" ? (
        <ClientDocumentsPanel clientId={clientId} client={client} requests={documentRequests} />
      ) : null}

      {tab === "messages" ? (
        <Panel title="Messages about this client">
          {messages.length ? (
            <ul className="list-none p-0 m-0 mb-4">
              {messages.map((message) => (
                <li key={message.id} className="py-2.5 border-b border-slate-100 last:border-0">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs font-semibold text-slate-700 m-0">
                      {message.direction === "PARTNER_TO_ADMIN"
                        ? "You"
                        : message.senderName || "The office"}
                    </p>
                    <p className="text-[11px] text-slate-400 m-0">
                      {formatDateTime(message.createdAt)}
                    </p>
                  </div>
                  <p className="text-sm text-slate-800 m-0 mt-1 whitespace-pre-wrap">
                    {message.body}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              title="No messages yet"
              description="Ask the office anything about this client here."
            />
          )}

          <div className="flex flex-col sm:flex-row gap-2 items-start">
            <TextField
              fullWidth
              multiline
              minRows={2}
              size="small"
              label="Write to the office"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
            />
            <button
              type="button"
              disabled={!draft.trim() || send.isLoading}
              onClick={() => send.mutate({ body: draft.trim(), clientId: Number(clientId) })}
              className="rounded-lg bg-teal-600 px-4 py-2 text-xs font-semibold text-white disabled:opacity-40"
            >
              {send.isLoading ? "Sending…" : "Send"}
            </button>
          </div>
          {send.isError ? (
            <p className="text-xs text-rose-700 mt-2 mb-0">That message did not send. Try again.</p>
          ) : null}
        </Panel>
      ) : null}
    </div>
  );
};

export default ClientDetailPage;
